import { Injectable, inject } from '@angular/core';
import { DynamicDynamicDialogService } from '@shared/components/ui/dynamic-dialog/dynamic-dialog.service';
import { Observable, finalize } from 'rxjs';
import { InputDialogComponent } from '../components/input-dialog/input-dialog.component';
import { ConfirmDialogComponent } from '../components/confirm-dialog/confirm-dialog.component';
import {
  MfaStepUpDialogComponent,
  MfaStepUpDialogData,
  MFA_STEP_UP_CONTEXT_ID,
  MFA_STEP_UP_MESSAGE_ID,
  MFA_STEP_UP_TITLE_ID,
} from '../components/mfa-step-up-dialog/mfa-step-up-dialog.component';

export interface InputDialogData {
  title: string;
  message: string;
  placeholder?: string;
  defaultValue?: string;
  inputType?: 'text' | 'number' | 'password' | 'email';
  required?: boolean;
  maxLength?: number;
  minLength?: number;
  pattern?: string;
  patternError?: string;
}

export interface ConfirmDialogData {
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  type?: 'info' | 'warning' | 'error' | 'success';
}

@Injectable({
  providedIn: 'root'
})
export class DynamicDialogService {
  private readonly dialogService = inject(DynamicDynamicDialogService);

  /**
   * Show an input dialog to get user input (replaces window.prompt)
   */
  prompt(data: InputDialogData): Observable<string | null> {
    const dialogRef = this.dialogService.open(InputDialogComponent, {
      header: data.title,
      width: '400px',
      closable: !data.required,
      closeOnEscape: !data.required,
      dismissableMask: !data.required,
      data
    });

    return dialogRef.onClose;
  }

  /**
   * Show the MFA step-up code dialog (login-styled Two-Factor Authentication
   * card). Resolves with the trimmed code, or `null` if the operator cancels.
   * The header is rendered inside the body, so the dialog chrome carries no
   * separate title bar — matching the login page's borderless card.
   */
  promptMfaCode(data: MfaStepUpDialogData): Observable<string | null> {
    const dialogRef = this.dialogService.open(MfaStepUpDialogComponent, {
      header: data.title,
      width: '400px',
      showHeader: false,
      closable: true,
      closeOnEscape: true,
      dismissableMask: false,
      // RF-QA-196: name the dialog by its in-body title and describe it by the
      // (optional) context line plus the explanatory paragraph.
      ariaLabelledBy: MFA_STEP_UP_TITLE_ID,
      ariaDescribedBy: data.context
        ? `${MFA_STEP_UP_CONTEXT_ID} ${MFA_STEP_UP_MESSAGE_ID}`
        : MFA_STEP_UP_MESSAGE_ID,
      data,
    });

    return dialogRef.onClose.pipe(finalize(() => dialogRef.close(null)));
  }

  /**
   * Show a confirmation dialog (replaces window.confirm)
   */
  confirm(data: ConfirmDialogData): Observable<boolean> {
    const dialogRef = this.dialogService.open(ConfirmDialogComponent, {
      header: data.title,
      width: '350px',
      closable: true,
      closeOnEscape: true,
      data
    });

    return dialogRef.onClose;
  }

  /**
   * Quick text input dialog
   */
  promptText(
    title: string,
    message: string,
    defaultValue: string = '',
    placeholder: string = ''
  ): Observable<string | null> {
    return this.prompt({
      title,
      message,
      defaultValue,
      placeholder,
      inputType: 'text'
    });
  }

  /**
   * Quick number input dialog
   */
  promptNumber(
    title: string,
    message: string,
    defaultValue: number = 0
  ): Observable<string | null> {
    return this.prompt({
      title,
      message,
      defaultValue: defaultValue.toString(),
      inputType: 'number'
    });
  }

  /**
   * Quick confirmation dialog
   */
  confirmAction(
    title: string,
    message: string,
    type: 'info' | 'warning' | 'error' | 'success' = 'info'
  ): Observable<boolean> {
    return this.confirm({
      title,
      message,
      type,
      confirmText: 'Confirm',
      cancelText: 'Cancel'
    });
  }

  /**
   * Warning confirmation dialog
   */
  confirmWarning(
    title: string,
    message: string
  ): Observable<boolean> {
    return this.confirm({
      title,
      message,
      type: 'warning',
      confirmText: 'Proceed',
      cancelText: 'Cancel'
    });
  }

  /**
   * Error confirmation dialog
   */
  confirmError(
    title: string,
    message: string
  ): Observable<boolean> {
    return this.confirm({
      title,
      message,
      type: 'error',
      confirmText: 'OK',
      cancelText: 'Cancel'
    });
  }
}
